import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useUserFormOptions } from '../../api/userFormOptionsQueries';
import { getCreationDto, getCreateUserDefaultValues } from '../../api/userMappers';
import { useCreateUserMutation } from '../../api/userMutations';
import type { UserAccessFormValues } from '../../types/UserAccessFormValues';
import { userAccessRoutes } from '../../utils/userAccessRoutes';
import { UserAccessForm } from '../user-access-form/UserAccessForm';
import { UserAccessHeader } from '../user-access-header/UserAccessHeader';
import { UserAccessErrorState } from '../user-access-state-panel/UserAccessErrorState';
import { UserAccessLoadingState } from '../user-access-state-panel/UserAccessLoadingState';
import { UserAccessNoCustomersState } from '../user-access-state-panel/UserAccessNoCustomersState';

export function CreateUserFormView() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { customers, roles, isPending, isError, retryOptions } = useUserFormOptions();
  const defaultCustomer = customers[0];
  const defaultValues = defaultCustomer ? getCreateUserDefaultValues(defaultCustomer) : undefined;
  async function onSuccess() {
    await navigate(userAccessRoutes.root);
  }
  const createUser = useCreateUserMutation(onSuccess);

  function handleSubmit(data: UserAccessFormValues) {
    createUser.mutate({ data: getCreationDto(data) });
  }

  function handleCancel() {
    navigate(userAccessRoutes.root);
  }

  const header = <UserAccessHeader title={t('userAccessControl.newUser')} onBack={handleCancel} />;

  if (isPending) {
    return <UserAccessLoadingState header={header} />;
  }

  if (isError) {
    return <UserAccessErrorState header={header} onRetry={() => void retryOptions()} />;
  }

  if (!defaultValues) {
    return <UserAccessNoCustomersState header={header} />;
  }

  return (
    <Stack width="100%" height="100%" minHeight={0} bgcolor="background.paper">
      {header}
      {/* React Hook Form applies default values on mount, so a new default customer needs a fresh form instance. */}
      <UserAccessForm
        key={`create-${defaultValues.customer.id}`}
        mode="create"
        initialValues={defaultValues}
        customers={customers}
        roles={roles}
        isPending={createUser.isPending}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    </Stack>
  );
}
